import { useCallback } from 'react'
import { isString, isNumber, isObject, transform, isEqual } from 'lodash'
import moment from 'moment'
import jwt_decode from 'jwt-decode'

/**
 * rem {convertedPxToRem}
 * @description - converted Px to Rem;
 *
 * */
export const rem = (...props) => props.map((prop) => +prop / 16 + 'rem ')

/**
 * parseJSON
 * @description - checks JSON for errors;
 *
 * */
export const parseJSON = (data) => {
	let value = false
	try {
		value = JSON.parse(data)
	} catch (err) {}
	return value
}
/**
 * postWindowMessage
 * @description - postWindowMessage;
 *
 * */
export const postWindowMessage = (
	wrapperUrl,
	path,
	key = 'returnadoKeyForPostMessage'
) => {
	const message = JSON.stringify({
		key: key,
		path,
	})
	window.top.postMessage(message, wrapperUrl)
}

/**
 * getUrlParameter {createTemporaryLinkElement}
 * @description - get Url parameters by name;
 *
 * */
export function getUrlParameter(name) {
	name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
	let regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
	let results = regex.exec(window.location.search)
	return results === null
		? ''
		: decodeURIComponent(results[1].replace(/\+/g, ' '))
}

/**
 * formatDate {Function}
 * @description - format Date
 *
 * */
export function formatDate(dateStr, mask = 'DD/MM/YYYY HH:mm') {
	let date
	if (isString(dateStr)) {
		date = moment(dateStr).format(mask)
	}

	if (isNumber(dateStr) && dateStr.toString().length === 10) {
		date = moment.unix(dateStr).format(mask)
	}

	return date
}

/**
 * langList {Function}
 * @description - convert option Language from server to array from React Select
 *
 * */
export function langList(string) {
	const options = [
		{ value: 'en', label: 'English' },
		{ value: 'sv', label: 'Svenska' },
		{ value: 'fr', label: 'Français' },
		{ value: 'es', label: 'Español' },
		{ value: 'it', label: 'Italiano' },
		{ value: 'de', label: 'Deutsch' },
		{ value: 'no', label: 'Norsk' },
		{ value: 'da', label: 'Dansk' },
		{ value: 'fi', label: 'Suomalainen' },
		{ value: 'nl', label: 'Nederlands' },
		{ value: 'ko', label: 'Korean' },
		{ value: 'jp', label: 'Japanese' },
	]
	const optionsMobile = [
		{ value: 'en', label: 'EN' },
		{ value: 'sv', label: 'SV' },
		{ value: 'fr', label: 'FR' },
		{ value: 'es', label: 'ES' },
		{ value: 'it', label: 'IT' },
		{ value: 'de', label: 'DE' },
		{ value: 'no', label: 'NO' },
		{ value: 'da', label: 'DA' },
		{ value: 'fi', label: 'FI' },
		{ value: 'nl', label: 'NL' },
		{ value: 'ko', label: 'KO' },
		{ value: 'jp', label: 'JP' },
	]
	//en,sv,de,no,da,fi
	let langList = window.innerWidth <= 551 ? optionsMobile : options
	return langList.filter((lang) => {
		if (string?.includes(lang.value)) return lang
	})
}

/**
 * useYupValidationResolver{validationSchema}
 * @description - The custom hook, by passing the validation schema;
 *
 * */
export const useYupValidationResolver = (validationSchema) =>
	useCallback(
		async (data) => {
			try {
				const values = await validationSchema.validate(data, {
					abortEarly: false,
				})

				return {
					values,
					errors: {},
				}
			} catch (errors) {
				return {
					values: {},
					errors: errors.inner.reduce(
						(allErrors, currentError) => ({
							...allErrors,
							[currentError.path]: {
								type: currentError.type ?? 'validation',
								message: currentError.message,
							},
						}),
						{}
					),
				}
			}
		},
		[validationSchema]
	)
/**
 * SS {Function}
 * @description - CRUD by SessionStorage
 * */
export const SS = (function () {
	function get(key) {
		return parseJSON(sessionStorage.getItem(key))
	}

	function set(key, val) {
		sessionStorage.setItem(key, JSON.stringify(val))
	}

	function remove(key) {
		sessionStorage.removeItem(key)
	}

	function clear() {
		sessionStorage.clear()
	}

	return {
		get,
		set,
		remove,
		clear,
	}
})()
/**
 * objToArr {Function}
 * @description - convert Object to Array
 * */
export const objToArr = (items) => {
	let arr = []
	for (let [flowKey, flowList] of Object.entries(items)) {
		if (flowKey !== 'newExchange')
			for (let [key, value] of Object.entries(flowList)) {
				arr.push({
					...value,
					flow: flowKey,
				})
			}
	}
	return arr
}
/**
 * getReclamationItemIndex {Function}
 * @description - get index based on
 * */
export const getReclamationItemIndex = (id, items) => {
	let index = 0

	for (let key in items) {
		if (key == id) {
			return index + 1
		}
		index += 1
	}

	return index
}

/**
 * getReclamationItemLength {Function}
 * @description - get reclamation items length
 * */
export const getReclamationItemLength = (refundItems) => {
	let l = 0
	for (let key in refundItems) {
		if (refundItems.hasOwnProperty(key)) {
			l += 1
		}
	}
	return l
}

/**
 * getItemsArray {Function}
 * @description - get reclamation items length
 * */
export const getItemsArray = (items) => {
	let itemsArr = []

	for (let key in items) {
		itemsArr.push(items[key])
	}

	return itemsArr
}

/**
 * getTokenData {Function}
 * @description - get parsed data from token
 *
 * */
export const getTokenData = (token) => {
	let tokenObject = SS.get('token')

	return tokenObject ? JSON.parse(jwt_decode(tokenObject.token).sub) : null
}

/**
 * parseTokenData {Function}
 * @description - get parsed data from token
 *
 * */
export const parseTokenData = (token) => {
	return JSON.parse(jwt_decode(token).sub)
}

/**
 * getShopPropertiesName {Function}
 * @description - get shop name
 *
 * */
export const getShopPropertiesName = (token) => {
	let shopProperties = SS.get('shopProperties')
	return shopProperties ? shopProperties['shop.name'] : ''
}

/**
 * getSupportEmail {Function}
 * @description - get shop name
 *
 * */
export const getSupportEmail = (token) => {
	let shopProperties = SS.get('shopProperties')
	return shopProperties ? shopProperties['shop.support.email'] : ''
}

/**
 * formatPrice {Function}
 * @description - get formatted price based on settings
 *
 * */
export const formatPrice = (price, currencyType = '') => {
	let shopProperties = SS.get('shopProperties')
	let defaultPrecision = shopProperties
		? parseInt(shopProperties['default.precision'])
		: 0
	let defaultDecimalsMethod = shopProperties
		? shopProperties['widget.show.decimals']
		: null
	let currency = currencyType
		? currencyType
		: shopProperties && shopProperties['default.currency']
		? shopProperties['default.currency']
		: ''

	if (defaultDecimalsMethod && defaultDecimalsMethod === 'round') {
		return `${price ? Math.round(price) : 0}${
			currency ? ' ' + currency : ''
		}`
	}

	if (defaultPrecision) {
		return `${price ? price.toFixed(defaultPrecision) : 0}${
			currency ? ' ' + currency : ''
		}`
	}
}

/**
 * stopPropagation {Function}
 * @description - stop event on inside element click
 *
 * */
export function stopPropagation(e) {
	e.stopPropagation()
	e.nativeEvent.stopImmediatePropagation()
}

/**
 * overflowBodyAdd {Function}
 * @description - add class
 *
 * */
export function overflowBodyAdd() {
	const target = document.getElementById('root')
	target.classList.add('overflow-hidden')
}

/**
 * overflowBodyRemove {Function}
 * @description - remove class
 *
 * */
export function overflowBodyRemove() {
	const target = document.getElementById('root')
	target.classList.remove('overflow-hidden')
}

/**
 * getIsComplaintRequestData {Function}
 * @description - check is Request Data is complaint or not
 *
 * */
export function getIsComplaintRequestData(
	requestData,
	typeKey = 'isComplaint'
) {
	let isComplaint = false

	requestData.returnOrder.items.forEach((item) => {
		if (item.returnAction === 'RECLAMATION') {
			isComplaint = true
		}
	})

	return {
		...requestData,
		[typeKey]: isComplaint,
	}
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object, base) {
	function changes(object, base) {
		return transform(object, function (result, value, key) {
			if (!isEqual(value, base[key])) {
				result[key] =
					isObject(value) && isObject(base[key])
						? changes(value, base[key])
						: value
			}
		})
	}
	return changes(object, base)
}
